// src/components/proposals/FoundationalEditor.jsx
//
// Settings-pane editor for Foundational Upgrade blocks. The proposal document
// renders these read-only — managing nested blocks/line items inline on an A4
// page would be unworkable — so all editing lives here.
//
// Add and edit share ONE component (LineItemFields). They previously diverged:
// the add form had placeholders and the inline rows had none, so the same item
// was described differently depending on how you reached it.
//
// Catalog search reuses Quoting's /api/ai/quote-suggest via a single injected
// function, so this file needs no knowledge of auth, suppliers or brands.
// Deliberately one-shot: query -> results -> add. No thread, no history.
import React, { useState } from "react";
import { CATEGORIES, DEFAULT_CATEGORY } from "./lineItemCategory";
import {
  makeFoundationalBlock,
  makeFoundationalLineItem,
  lineItemFromSuggestion,
  computeBlockSubtotal,
  lineItemTotal,
  priceFromCost,
  fmtMoney,
} from "./foundationalBlocks";

/* ── Decimal text entry ────────────────────────────────────────────────────
   Deliberately NOT <input type="number">:
     - no spinner arrows
     - starts genuinely empty rather than at a literal 0, which is what caused
       typing "10" into a zeroed field to read "010"
     - keeps the raw string while focused so it can't fight the user
       mid-keystroke, and only normalises on blur
   Accepts digits and a single decimal point; everything else is dropped. */
function DecimalInput({ value, onCommit, placeholder = "0.00", prefix, decimals = 2,
                        disabled, style, ariaLabel }) {
  const [draft, setDraft] = useState(null);          // non-null only while editing
  const shown = draft !== null ? draft : (value === "" || value == null ? "" : String(value));

  const sanitise = (raw) => {
    let s = String(raw).replace(/[^0-9.]/g, "");
    const first = s.indexOf(".");
    if (first !== -1) {                               // keep only the first dot
      s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, "");
    }
    return s;
  };

  const commit = () => {
    const s = draft === null ? shown : draft;
    if (s === "" || s === ".") { onCommit(""); setDraft(null); return; }
    const n = parseFloat(s);
    if (!Number.isFinite(n)) { onCommit(""); setDraft(null); return; }
    // Quantity keeps its natural form (2.5 stays 2.5); money settles to 2dp.
    onCommit(decimals === null ? n : Number(n.toFixed(decimals)));
    setDraft(null);
  };

  return (
    <div style={{ position: "relative", ...style }}>
      {prefix && (
        <span style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)",
                       fontSize: 11, color: "var(--text3)", pointerEvents: "none" }}>
          {prefix}
        </span>
      )}
      <input
        className="inp" type="text" inputMode="decimal" disabled={disabled}
        aria-label={ariaLabel} placeholder={placeholder} value={shown}
        onChange={(e) => setDraft(sanitise(e.target.value))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        style={{ marginBottom: 0, fontSize: 11, textAlign: "right",
                 paddingLeft: prefix ? 16 : undefined }}
      />
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{ display: "block", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                    textTransform: "uppercase", color: "var(--text3)", marginBottom: 2 }}>
      {children}
    </label>
  );
}

/* The one item form, used for both adding and editing. `li` carries raw values;
   every change is handed straight back so the caller decides whether that means
   local draft state (add) or an immediate commit (edit). */
function LineItemFields({ li, onPatch, markupPct, priceIsAuto, onPriceEdited, disabled }) {
  const total = lineItemTotal({ quantity: li.quantity, unitPrice: li.unitPrice });

  return (
    <>
      <div style={{ marginBottom: 6 }}>
        <FieldLabel>Item</FieldLabel>
        <input className="inp" type="text" disabled={disabled}
               style={{ marginBottom: 0, fontSize: 11 }}
               placeholder="e.g. Labour — on-site installation"
               value={li.description || ""}
               onChange={(e) => onPatch({ description: e.target.value })} />
      </div>

      {/* ⚠ Sits directly under the item description, because the description
          is what a suggestion was read FROM — the two belong side by side when
          somebody is checking whether the guess was right. */}
      <div style={{ marginBottom: 6 }}>
        <FieldLabel>
          Category
          {li.category && !li.categoryConfirmed && (
            <span style={{ marginLeft: 6, fontWeight: 700, color: "#b45309",
                           textTransform: "none", letterSpacing: 0 }}>
              · suggested, please check
            </span>
          )}
        </FieldLabel>
        <select className="inp" disabled={disabled}
                style={{ marginBottom: 0, fontSize: 11,
                         borderColor: li.category && !li.categoryConfirmed ? "#f59e0b" : undefined }}
                value={li.category || DEFAULT_CATEGORY}
                title="Only Labour lines count toward the contract's estimated hours."
                /* ⚠ Touching it CONFIRMS it, even when the value does not
                   change — agreeing with a suggestion is a decision, and it is
                   the only way somebody can clear the flag on a guess that was
                   already right. */
                onChange={(e) => onPatch({ category: e.target.value, categoryConfirmed: true })}>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 6 }}>
        <FieldLabel>Details</FieldLabel>
        <textarea className="inp" rows={2} disabled={disabled}
                  style={{ marginBottom: 0, fontSize: 11, resize: "vertical" }}
                  placeholder="Optional — appears under the item on the proposal"
                  value={li.detail || ""}
                  onChange={(e) => onPatch({ detail: e.target.value })} />
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <FieldLabel>Quantity</FieldLabel>
          <DecimalInput value={li.quantity} disabled={disabled} placeholder="1"
                        decimals={null} ariaLabel="Quantity"
                        onCommit={(v) => onPatch({ quantity: v === "" ? 1 : v })} />
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel>Unit Cost</FieldLabel>
          <DecimalInput value={li.unitCost} disabled={disabled} prefix="$" ariaLabel="Unit cost"
                        onCommit={(v) => {
                          const patch = { unitCost: v === "" ? 0 : v };
                          // Price follows cost only until the user sets one.
                          if (priceIsAuto) patch.unitPrice = priceFromCost(v || 0, markupPct);
                          onPatch(patch);
                        }} />
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel>Unit Price</FieldLabel>
          <DecimalInput value={li.unitPrice} disabled={disabled} prefix="$" ariaLabel="Unit price"
                        onCommit={(v) => { onPatch({ unitPrice: v === "" ? 0 : v });
                                           if (onPriceEdited) onPriceEdited(); }} />
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel>Total</FieldLabel>
          {/* Computed, never editable — qty x unit price. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", textAlign: "right",
                        padding: "5px 0", whiteSpace: "nowrap" }}>
            {fmtMoney(total)}
          </div>
        </div>
      </div>
    </>
  );
}

export default function FoundationalEditor({ c, set, disabled, markupPct = 0, onCatalogSearch }) {
  const blocks = Array.isArray(c.foundationalBlocks) ? c.foundationalBlocks : [];

  const setBlocks = (next) => { if (!disabled) set({ foundationalBlocks: next }); };
  const updateBlock = (id, patch) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const deleteBlock = (id) => setBlocks(blocks.filter((b) => b.id !== id));
  const addBlock = () => setBlocks([...blocks, makeFoundationalBlock({ title: "" })]);

  return (
    <>
      {blocks.map((block, idx) => (
        <BlockEditor
          key={block.id}
          block={block}
          index={idx}
          disabled={disabled}
          markupPct={markupPct}
          onCatalogSearch={onCatalogSearch}
          onUpdate={(patch) => updateBlock(block.id, patch)}
          onDelete={() => deleteBlock(block.id)}
          onSetItems={(items) => updateBlock(block.id, { lineItems: items })}
        />
      ))}

      <button className="btn" style={{ fontSize: 10, width: "100%" }} disabled={disabled}
              onClick={addBlock}>
        + Add Foundational Block
      </button>
      {blocks.length === 0 && (
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6 }}>
          Each block appears on the proposal as its own separately-quoted project.
        </div>
      )}
    </>
  );
}

function BlockEditor({ block, index, disabled, markupPct, onCatalogSearch,
                       onUpdate, onDelete, onSetItems }) {
  const [mode, setMode] = useState(null);            // null | "manual" | "search"
  const [openItem, setOpenItem] = useState(null);    // id of the row being edited
  const items = Array.isArray(block.lineItems) ? block.lineItems : [];
  const subtotal = computeBlockSubtotal(block);

  const addItem = (li) => onSetItems([...items, li]);
  const patchItem = (id, patch) =>
    onSetItems(items.map((li) => (li.id === id ? { ...li, ...patch } : li)));
  const removeItem = (id) => onSetItems(items.filter((li) => li.id !== id));

  /* Swap with the neighbour — same shape as the Lead panel's term reorder.
     Line item order is the order they print in the block, so this is the only
     way to control how a quoted project reads. Bounds-checked as well as
     disabled at the ends: the buttons are the only caller today, but a swap
     past either end would silently corrupt the list rather than no-op. */
  const moveItem = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onSetItems(next);
  };

  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 6,
                  padding: 10, marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: "var(--strat)",
                       letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Block {index + 1}
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn" style={{ fontSize: 9, padding: "2px 7px" }} disabled={disabled}
                onClick={onDelete}>✕</button>
      </div>

      <input className="inp" style={{ marginBottom: 5, fontSize: 12, fontWeight: 600 }}
             disabled={disabled} placeholder="Block title (e.g. New Starter Office Setup)"
             value={block.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} />
      <textarea className="inp" rows={2} disabled={disabled}
                style={{ marginBottom: 5, fontSize: 12, resize: "vertical" }}
                placeholder="Optional context for this block"
                value={block.description || ""}
                onChange={(e) => onUpdate({ description: e.target.value })} />
      <input className="inp" style={{ marginBottom: 8, fontSize: 12 }} disabled={disabled}
             placeholder="Optional timeline (e.g. Within 30 days)"
             value={block.timeline || ""} onChange={(e) => onUpdate({ timeline: e.target.value })} />

      {items.length === 0 ? (
        <div style={{ padding: "10px 0", textAlign: "center", color: "var(--text3)", fontSize: 11 }}>
          No items yet — add manually or search the catalog.
        </div>
      ) : (
        items.map((li, i) => (
          <div key={li.id} style={{ border: "1px solid var(--border)", borderRadius: 5,
                                    padding: 8, marginBottom: 6, background: "var(--s2)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "var(--text)", fontWeight: 600 }}>
                  {li.description || "(untitled item)"}
                </div>
                {li.detail && (
                  <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 1 }}>{li.detail}</div>
                )}
                <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 2 }}>
                  {[li.supplier, li.sku, li.stockNote].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>
                  {fmtMoney(lineItemTotal(li))}
                </div>
                <div style={{ fontSize: 9, color: "var(--text3)" }}>
                  {li.quantity} × {fmtMoney(li.unitPrice)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <button className="btn" style={{ fontSize: 9, padding: "1px 6px" }} disabled={disabled}
                        onClick={() => setOpenItem(openItem === li.id ? null : li.id)}>
                  {openItem === li.id ? "Close" : "Edit"}
                </button>
                <button className="btn" style={{ fontSize: 9, padding: "1px 6px" }} disabled={disabled}
                        onClick={() => removeItem(li.id)}>✕</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <button className="btn" style={{ fontSize: 9, padding: "1px 6px" }}
                        disabled={disabled || i === 0}
                        title="Move up" aria-label="Move item up"
                        onClick={() => moveItem(i, -1)}>↑</button>
                <button className="btn" style={{ fontSize: 9, padding: "1px 6px" }}
                        disabled={disabled || i === items.length - 1}
                        title="Move down" aria-label="Move item down"
                        onClick={() => moveItem(i, 1)}>↓</button>
              </div>
            </div>

            {openItem === li.id && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                {/* Editing an existing item never re-derives price from cost —
                    whatever is stored was already decided. */}
                <LineItemFields li={li} disabled={disabled} markupPct={markupPct}
                                priceIsAuto={false}
                                onPatch={(patch) => patchItem(li.id, patch)} />
              </div>
            )}
          </div>
        ))
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 6, marginBottom: 6 }}>
        <button className="btn" style={{ fontSize: 10 }} disabled={disabled}
                onClick={() => setMode(mode === "manual" ? null : "manual")}>+ Manual item</button>
        <button className="btn btn-strat" style={{ fontSize: 10 }}
                disabled={disabled || !onCatalogSearch}
                title={onCatalogSearch ? "" : "Catalog search unavailable here"}
                onClick={() => setMode(mode === "search" ? null : "search")}>🔍 Search catalog</button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>{fmtMoney(subtotal)}</span>
      </div>

      {mode === "manual" && (
        <ManualItemForm markupPct={markupPct}
                        onAdd={(li) => { addItem(li); setMode(null); }}
                        onCancel={() => setMode(null)} />
      )}
      {mode === "search" && onCatalogSearch && (
        <CatalogSearch markupPct={markupPct} existingItems={items}
                       onCatalogSearch={onCatalogSearch}
                       onAdd={addItem} onClose={() => setMode(null)} />
      )}
    </div>
  );
}

function ManualItemForm({ markupPct, onAdd, onCancel }) {
  // Money starts empty, not 0 — a literal 0 in the box is what produced "010".
  const [draft, setDraft] = useState(() => ({
    description: "", detail: "", quantity: 1, unitCost: "", unitPrice: "",
  }));
  const [priceIsAuto, setPriceIsAuto] = useState(true);

  return (
    <div style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 6,
                  padding: 8, marginBottom: 6 }}>
      <LineItemFields
        li={draft}
        markupPct={markupPct}
        priceIsAuto={priceIsAuto}
        onPriceEdited={() => setPriceIsAuto(false)}
        onPatch={(patch) => setDraft((d) => ({ ...d, ...patch }))}
      />
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
        <button className="btn btn-strat" style={{ fontSize: 10 }}
                disabled={!String(draft.description || "").trim()}
                onClick={() => onAdd(makeFoundationalLineItem({
                  description: draft.description.trim(),
                  detail: (draft.detail || "").trim(),
                  quantity: Number(draft.quantity) || 1,
                  unitCost: Number(draft.unitCost) || 0,
                  unitPrice: Number(draft.unitPrice) || 0,
                }))}>Add</button>
        <button className="btn" style={{ fontSize: 10 }} onClick={onCancel}>Cancel</button>
        <span style={{ fontSize: 9, color: "var(--text3)" }}>
          {priceIsAuto ? `price auto @ ${markupPct}% markup` : "price overridden"}
        </span>
      </div>
    </div>
  );
}

function CatalogSearch({ markupPct, existingItems, onCatalogSearch, onAdd, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [ran, setRan] = useState(false);

  const run = async () => {
    if (!query.trim() || loading) return;
    setLoading(true); setErr(null);
    try {
      // Each search REPLACES the previous results — one-shot, no thread.
      const suggestions = await onCatalogSearch(query.trim(), existingItems);
      setResults(Array.isArray(suggestions) ? suggestions : []);
    } catch (e) {
      setResults([]);
      setErr(e?.message || "Search failed");
    } finally {
      setLoading(false); setRan(true);
    }
  };

  return (
    <div style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 6,
                  padding: 8, marginBottom: 6 }}>
      <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
        <input className="inp" style={{ marginBottom: 0, fontSize: 11, flex: 1 }}
               placeholder="e.g. Fortigate 60F, or 5x HP mini PC 16GB"
               value={query} onChange={(e) => setQuery(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") run(); }} />
        <button className="btn btn-strat" style={{ fontSize: 10 }} disabled={loading} onClick={run}>
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      {err && <div style={{ fontSize: 10, color: "#b91c1c", marginBottom: 6 }}>{err}</div>}
      {!loading && !err && ran && results.length === 0 && (
        <div style={{ fontSize: 10, color: "var(--text3)", textAlign: "center", padding: 6 }}>
          No matches in the priority catalog. Try different wording, or add the item manually.
        </div>
      )}
      {!ran && !loading && (
        <div style={{ fontSize: 10, color: "var(--text3)", textAlign: "center", padding: 6 }}>
          Searches Leader / Dicker. Results replace each other — nothing is kept between searches.
        </div>
      )}

      {results.map((r, i) => (
        <div key={r.chatItemId || r.sku || i}
             style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      gap: 8, padding: 6, border: "1px solid var(--border)", borderRadius: 5,
                      marginBottom: 5, background: "var(--s1)" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)" }}>{r.description}</div>
            <div style={{ fontSize: 9, color: "var(--text3)" }}>
              {[r.supplier, r.sku, r.stockNote].filter(Boolean).join(" · ")}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)" }}>
              {fmtMoney(r.unitPrice != null ? r.unitPrice : priceFromCost(r.unitCost, markupPct))}
            </div>
          </div>
          <button className="btn" style={{ fontSize: 10, flexShrink: 0 }}
                  onClick={() => onAdd(lineItemFromSuggestion(r, markupPct))}>+ Add</button>
        </div>
      ))}

      <button className="btn" style={{ fontSize: 10, marginTop: 2 }} onClick={onClose}>Done</button>
    </div>
  );
}
